import { MapViewBounds } from '@/types/extendedMapOptions';

/**
 * Converts from degrees to radians.
 * @param {number} radians
 * @returns {number}
 */
export function toDegrees(radians: number): number {
  return (radians * 180) / Math.PI;
}

/**
 * Converts from radians to degrees.
 * @param {number} degrees
 * @returns {number}
 */
export function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180.0;
}

function fmod(angle: number, start: number, end: number): number {
  end -= start;
  return ((((angle - start) % end) + end) % end) + start;
}

/**
 * Returns the heading from one GeoCoordinate to another GeoCoordinate. Headings are expressed
 * in degrees clockwise from North within the range [-180, 180).
 * @param {number} startLatitude The starting latitude.
 * @param {number} startLongitude The starting longitude.
 * @param {number} endLatitude The ending latitude.
 * @param {number} endLongitude The ending longitude.
 * @returns {number} The heading in degrees.
 */
export function computeHeading(startLatitude: number, startLongitude: number, endLatitude: number, endLongitude: number): number {
  const fromLat = toRadians(startLatitude);
  const toLat = toRadians(endLatitude);
  const deltaLng = toRadians(endLongitude) - toRadians(startLongitude);

  const angle = toDegrees(
    Math.atan2(
      Math.sin(deltaLng) * Math.cos(toLat),
      Math.cos(fromLat) * Math.sin(toLat) - Math.sin(fromLat) * Math.cos(toLat) * Math.cos(deltaLng)
    )
  );

  return fmod(angle, -180, 180);
}

/**
 * Calculate the bounds of the map view for the given latitudes and longitudes.
 * @param {number[]} latitudes an array of latitudes.
 * @param {number[]} longitudes an array of longitudes.
 * @returns {MapViewBounds}
 */
export function calculateBounds(latitudes: number[], longitudes: number[]): MapViewBounds {
  let latitudesCount = latitudes.length;
  let longitudesCount = longitudes.length;
  let maxLatitude = -Infinity;
  let minLatitude = Infinity;
  let maxLongitude = -Infinity;
  let minLongitude = Infinity;

  while (latitudesCount--) {
    maxLatitude = latitudes[latitudesCount] > maxLatitude ? latitudes[latitudesCount] : maxLatitude;
    minLatitude = latitudes[latitudesCount] < minLatitude ? latitudes[latitudesCount] : minLatitude;
  }

  while (longitudesCount--) {
    maxLongitude = longitudes[longitudesCount] > maxLongitude ? longitudes[longitudesCount] : maxLongitude;
    minLongitude = longitudes[longitudesCount] < minLongitude ? longitudes[longitudesCount] : minLongitude;
  }

  const bounds: MapViewBounds = {
    north: maxLatitude,
    east: maxLongitude,
    south: minLatitude,
    west: minLongitude
  };

  return bounds;
}

export default { computeHeading, calculateBounds };
